import React, { useState } from 'react'
import type { ReactElement } from 'react'

import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import { QRCodeSVG } from 'qrcode.react'

import {
  atomMeditating,
  lunchIcon,
  person1,
  person2,
  person3,
  walmartSpark,
} from '@ui/assets'
import {
  BankLevelSecurity,
  BrighterFuture,
  FAQLodge,
  faqsData,
  FeaturesGrid,
  HowToStart,
  InstantCash,
  PersonalizedEarning,
  Testimonials,
  TransformCTA,
  TrustedBy,
} from '@ui/components/lodge'
import { Seo } from '@ui/components/seo'
import { StoreRatingsPill } from '@ui/components/StoreRatingsPill'
import { BRANCH_DOWNLOAD_LINK } from '@ui/constants'
import { MarketingLayout } from '@ui/layout/MarketingLayout'

const title = 'ATM Instant Cash™ & Rewards - Earn, Budget, Save & Access Cash*'
const description =
  'Discover how ATM.com helps you earn extra cash, manage your finances, and access instant cash advances when you need them most. Join the Ant Colony and start earning rewards, budgeting smarter, and accessing emergency funds today.'

export function Index() {
  const heroRef = React.useRef<HTMLDivElement>(null)
  const [blobPosition, setBlobPosition] = useState({
    y: 350,
    opacity: 1,
    scale: 1.2,
    isFixed: true,
  })
  const [taglineArrowOffset, setTaglineArrowOffset] = useState(400)
  const [headlineTransform, setHeadlineTransform] = useState({
    y: 0,
    opacity: 1,
  })
  const [taglineState, setTaglineState] = useState({
    opacity: 0,
    isFixed: true,
  })
  const [cardAnimations, setCardAnimations] = useState({
    card1: { opacity: 1, transform: 'translateY(0px) rotate(-8deg)' },
    card2: { opacity: 1, transform: 'translateY(0px) rotate(-5deg)' },
    card3: { opacity: 1, transform: 'translateY(0px) rotate(5deg)' },
    person1: { opacity: 1, transform: 'translateY(0px)' },
    person2: { opacity: 1, transform: 'translateY(0px)' },
    person3: { opacity: 1, transform: 'translateY(0px)' },
  })

  // Parallax scroll effect for blob character and squiggly arrow - desktop only
  React.useEffect(() => {
    const handleScroll = () => {
      // Only apply parallax on desktop (lg breakpoint = 1024px)
      if (window.innerWidth < 1024) {
        return
      }

      const scrollY = window.scrollY
      const viewportHeight = window.innerHeight

      // Get hero container position
      const heroContainer = heroRef.current
      const heroRect = heroContainer?.getBoundingClientRect()
      const heroBottom = heroRect ? scrollY + heroRect.bottom : 1400

      // Headline fade and scroll off aggressively
      const headlineFadeDistance = viewportHeight * 0.4
      let headlineY = 0
      let headlineOpacity = 1

      if (scrollY < headlineFadeDistance) {
        const progress = scrollY / headlineFadeDistance
        headlineY = -(progress * 200) // Move up aggressively
        headlineOpacity = 1 - progress * 1.2 // Fade faster than movement
      } else {
        headlineY = -200
        headlineOpacity = 0
      }

      setHeadlineTransform({
        y: headlineY,
        opacity: Math.max(0, headlineOpacity),
      })

      // Calculate y for fixed positioning (bottom: -y)
      // Start: y=350 → bottom:-350px (peeking)
      // Rest: y=180 → bottom:-180px (LOWER on screen, near bottom edge, fully visible)
      let translateY = 350 // Start peeking
      const opacity = 1
      let scale = 1.2 // Start bigger
      let isFixed = true
      let taglineIsFixed = true

      const quickShrinkDuration = viewportHeight * 0.15 // Very quick shrink

      // Tagline squiggly arrow timing - calculate first so we know when it finishes
      const taglineArrowStart = quickShrinkDuration + viewportHeight * 0.1 // Start shortly after reaching resting position
      const taglineArrowDuration = viewportHeight * 0.8 // Animate over 80vh
      const taglineArrowEnd = taglineArrowStart + taglineArrowDuration

      // Tagline fades in after headline/cards start fading (delayed start)
      // STAYS at full opacity until squiggly finishes drawing
      let taglineOpacity = 0
      const taglineFadeStart = headlineFadeDistance * 0.5 // Start halfway through headline fade
      const taglineFadeDuration = headlineFadeDistance * 0.5

      if (scrollY < taglineFadeStart) {
        taglineOpacity = 0
      } else if (scrollY < headlineFadeDistance) {
        const progress = (scrollY - taglineFadeStart) / taglineFadeDuration
        taglineOpacity = progress * 1.2 // Fade in quickly
      } else if (scrollY < taglineArrowEnd) {
        taglineOpacity = 1 // Stay at full opacity while squiggly draws
      } else {
        taglineOpacity = 1 // Keep visible after squiggly completes
      }

      // Calculate when to switch to absolute: when container bottom - 180px reaches viewport bottom
      // This ensures seamless transition with no jump
      const switchToAbsoluteAt = heroBottom - 180 - viewportHeight

      if (scrollY < quickShrinkDuration) {
        // Phase 1: Quick shrink and reveal, move to resting position
        const progress = scrollY / quickShrinkDuration
        translateY = 350 - progress * 170 // Move from 350 to 180 (peeking to lower on screen)
        scale = 1.2 - progress * 0.2 // Shrink from 1.2 to 1.0
      } else if (scrollY < switchToAbsoluteAt) {
        // Phase 2: Stay FIXED at bottom position until squiggly completes
        translateY = 180
        scale = 1
      } else {
        // Phase 3: Stuck to container bottom - use absolute positioning
        isFixed = false
        taglineIsFixed = false
        translateY = 180 // Same value, now relative to container bottom
        scale = 1
      }

      // Tagline squiggly arrow animates during the fixed resting period
      let taglineArrowOffset = 400

      if (scrollY < taglineArrowStart) {
        taglineArrowOffset = 400 // Hidden
      } else if (scrollY < taglineArrowEnd) {
        const progress = (scrollY - taglineArrowStart) / taglineArrowDuration
        taglineArrowOffset = 400 - 400 * progress // Unmask progressively
      } else {
        taglineArrowOffset = 0 // Fully visible
      }

      setBlobPosition({ y: translateY, opacity, scale, isFixed })
      setTaglineState({
        opacity: Math.min(1, taglineOpacity),
        isFixed: taglineIsFixed,
      })
      setTaglineArrowOffset(taglineArrowOffset)

      // Card animations - fade out on same timing as headline, with staggered upward movement
      const cardFadeDistance = headlineFadeDistance // Same as headline: viewportHeight * 0.4

      const getCardFlyoffAnimation = (baseRotation: number, delay: number) => {
        // Opacity fades on same clock as headline
        let opacity = 1
        if (scrollY < cardFadeDistance) {
          const fadeProgress = scrollY / cardFadeDistance
          opacity = 1 - fadeProgress * 1.2 // Aggressive fade like headline
        } else {
          opacity = 0
        }

        // Movement starts with delay and moves upward
        const moveStart = delay * 200
        const moveEnd = moveStart + viewportHeight * 0.4
        let moveY = 0

        if (scrollY > moveStart && scrollY < moveEnd) {
          const moveProgress = (scrollY - moveStart) / (moveEnd - moveStart)
          moveY = -(moveProgress * 600)
        } else if (scrollY >= moveEnd) {
          moveY = -600
        }

        return {
          opacity: Math.max(0, opacity),
          transform: `translateY(${moveY}px) rotate(${baseRotation}deg)`,
        }
      }

      const getPersonFlyoffAnimation = (delay: number) => {
        // Opacity fades on same clock as headline
        let opacity = 1
        if (scrollY < cardFadeDistance) {
          const fadeProgress = scrollY / cardFadeDistance
          opacity = 1 - fadeProgress * 1.2 // Aggressive fade like headline
        } else {
          opacity = 0
        }

        // Movement starts with delay and moves upward
        const moveStart = delay * 200
        const moveEnd = moveStart + viewportHeight * 0.4
        let moveY = 0

        if (scrollY > moveStart && scrollY < moveEnd) {
          const moveProgress = (scrollY - moveStart) / (moveEnd - moveStart)
          moveY = -(moveProgress * 600)
        } else if (scrollY >= moveEnd) {
          moveY = -600
        }

        return {
          opacity: Math.max(0, opacity),
          transform: `translateY(${moveY}px)`,
        }
      }

      setCardAnimations({
        card1: getCardFlyoffAnimation(-8, 0),
        card2: getCardFlyoffAnimation(-5, 0.3),
        card3: getCardFlyoffAnimation(5, 0.6),
        person1: getPersonFlyoffAnimation(0.9),
        person2: getPersonFlyoffAnimation(1.2),
        person3: getPersonFlyoffAnimation(1.5),
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial call

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqsData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }

  return (
    <>
      <Seo title={title} description={description} />
      <Head>
        <meta name="fo-verify" content="cff45dd5-a40e-4091-b715-457f39173eec" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </Head>
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      <div
        ref={heroRef}
        className="relative min-h-screen overflow-hidden bg-[#e6f7ff] pb-16 pt-12"
      >
        {/* Decorative circles */}
        <div className="absolute right-0 top-1/3 h-[1500px] w-[1500px] rounded-full bg-[#d4f1ff]/30" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 flex items-center justify-center">
            <StoreRatingsPill />
          </div>

          {/* Hero Content */}
          <div className="mb-12 text-center">
            <h1 className="font-display mx-auto mb-6 max-w-5xl text-[56px] leading-[0.95] tracking-tight text-[#0a2d4a] md:text-7xl lg:text-[90px]">
              <span
                className="inline-block lg:transition-all lg:duration-100 lg:ease-out"
                style={{
                  transform: `translateY(${headlineTransform.y}px)`,
                  opacity: headlineTransform.opacity,
                }}
              >
                Cash wherever you are.
                <br />
                <span className="text-[#29bfed]">Whenever you need it.</span>
              </span>
            </h1>
          </div>

          {/* Ant Mascot and Floating Cards */}
          <div className="relative mx-auto max-w-7xl lg:min-h-[2200px]">
            {/* Tagline that fades in above atom - hidden on mobile, parallax on desktop */}
            <div
              className={`hidden lg:block ${taglineState.isFixed ? 'lg:fixed' : 'lg:absolute'} left-1/2 text-center`}
              style={{
                bottom: taglineState.isFixed ? '700px' : '880px',
                transform: 'translateX(-50%)',
                opacity: taglineState.opacity,
                pointerEvents: 'none',
                width: '1152px', // max-w-6xl = 72rem = 1152px
                maxWidth: '90vw',
              }}
            >
              <h2 className="font-display text-5xl leading-[0.95] tracking-tight text-[#0a2d4a] md:text-6xl lg:text-[80px]">
                we make it easy for you to
                <br />
                earn extra cash while
                <br />
                <span className="relative inline-block text-[#29bfed]">
                  managing your finances
                  {/* Squiggly decoration below "finances" with unmask animation */}
                  <span className="absolute right-8 top-full mt-0 inline-block w-[209px]">
                    <svg
                      preserveAspectRatio="none"
                      width="100%"
                      height="100%"
                      overflow="visible"
                      style={{ display: 'block' }}
                      viewBox="0 0 213 30"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2.00051 26.2206L16.5832 19.871C25.2847 16.0822 34.6119 13.6819 44.0933 14.0993C48.0704 14.2744 49.8913 14.6246 51.3546 15.0151C53.9066 15.6961 55.441 16.889 56.4289 18.3343C59.211 22.4043 58.5586 27.0779 58.5936 27.6096C58.6482 28.4389 60.6681 24.4803 63.9438 20.9552C65.8689 18.8836 68.6902 16.5898 71.0778 14.8729C73.4653 13.156 75.4263 12.1755 77.592 11.3202C79.7578 10.4649 82.0689 9.7646 84.31 9.22873C86.5511 8.69286 88.6521 8.34268 90.715 8.16229C92.7778 7.9819 94.7388 7.9819 96.6244 8.12197C100.275 8.39315 103.395 9.25101 105.832 10.1328C107.869 10.8701 109.064 12.15 109.806 13.279C111.057 15.1833 110.766 16.889 110.941 17.3888C111.327 18.4892 112.528 14.4293 114.219 12.2784C115.167 11.0728 116.471 9.77733 118.101 8.46153C119.73 7.14573 121.691 5.88511 123.752 4.85051C125.813 3.81592 127.914 3.04554 130.187 2.57864C132.46 2.11175 134.841 1.97168 137.223 2.00457C139.605 2.03747 141.916 2.24757 144.158 2.60093C146.399 2.95428 148.5 3.44452 150.387 4.04724C153.944 5.18275 156.526 6.77222 158.292 8.21853C159.86 9.50313 160.635 11.8593 161.622 13.5602C162.271 14.679 164.871 11.7404 167.831 9.83569C169.488 8.77015 171.632 7.7909 174.08 6.86453C176.528 5.93817 179.26 5.16779 182.137 4.63086C185.015 4.09393 187.957 3.81379 190.698 3.80955C193.438 3.8053 195.89 4.08544 198.098 4.47488C202.227 5.20301 205.196 6.34989 207.173 7.37281C208.099 7.9055 208.869 8.46577 209.336 8.85945C209.803 9.25313 209.943 9.46323 210.088 9.6797"
                        stroke="#29BFED"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray="400"
                        strokeDashoffset={taglineArrowOffset}
                      />
                    </svg>
                  </span>
                </span>
              </h2>
            </div>

            {/* Mobile: Simple static layout with floating cards */}
            <div className="relative lg:hidden" style={{ minHeight: '200px' }}>
              {/* Atom - on top */}
              <div className="relative z-20 mx-auto w-full max-w-md">
                <Image
                  src={atomMeditating}
                  width={1207}
                  height={1207}
                  alt="ATM Atom Mascot"
                  className="mx-auto"
                  priority
                />
              </div>

              {/* Person Images */}
              <div
                className="absolute right-4 top-16"
                style={{ width: '90px' }}
              >
                <Image
                  src={person1}
                  width={150}
                  height={157}
                  alt=""
                  className="rounded-xl"
                />
              </div>

              <div
                className="absolute left-4 top-64"
                style={{ width: '130px' }}
              >
                <Image
                  src={person2}
                  width={245}
                  height={245}
                  alt=""
                  className="rounded-xl"
                />
              </div>

              <div
                className="absolute right-4 top-80"
                style={{ width: '140px' }}
              >
                <Image
                  src={person3}
                  width={267}
                  height={292}
                  alt=""
                  className="rounded-xl"
                  style={{ transform: 'rotate(25deg)' }}
                />
              </div>

              {/* Walmart Card */}
              <div
                className="absolute left-4 top-4"
                style={{ transform: 'rotate(-8deg)' }}
              >
                <div className="flex items-center gap-2 rounded-xl bg-white px-2 py-2 shadow-lg">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0053e2] p-3">
                    <Image
                      src={walmartSpark}
                      width={33}
                      height={37}
                      alt="Walmart"
                    />
                  </div>
                  <p className="font-display pr-2 text-sm text-[#0a2d4a]">
                    Get 1% cash back
                  </p>
                </div>
              </div>

              {/* In-n-Out Card - hidden on mobile */}
              <div
                className="absolute right-0 top-96 hidden lg:block"
                style={{ transform: 'rotate(5deg)' }}
              >
                <div className="flex items-center gap-4 rounded-xl bg-white px-3 py-3 shadow-lg">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e1f3e3] p-4">
                    <Image
                      src={lunchIcon}
                      width={28}
                      height={28}
                      alt="Restaurant"
                    />
                  </div>
                  <div className="flex items-center gap-8">
                    <div>
                      <p className="font-display text-lg text-[#0a3754]">
                        In-n-Out
                      </p>
                      <p className="text-sm text-[#0a3754]/50">Jan 12</p>
                    </div>
                    <p className="font-display text-lg text-[#0a3754]">
                      $36.12
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop: Parallax atom */}
            <div
              className={`hidden lg:block ${blobPosition.isFixed ? 'lg:fixed' : 'lg:absolute'} left-1/2 -translate-x-1/2`}
              style={{
                bottom: blobPosition.isFixed ? `${-blobPosition.y}px` : `0px`,
                transform: `translateX(-50%) scale(${blobPosition.scale})`,
                opacity: blobPosition.opacity,
                width: '800px',
                maxWidth: '90vw',
                pointerEvents: 'none',
                zIndex: 30,
              }}
            >
              <Image
                src={atomMeditating}
                width={1207}
                height={1207}
                alt="ATM Atom Mascot"
                className="mx-auto"
                priority
              />
            </div>

            {/* Floating Person Images - spread throughout container */}
            <div
              className="absolute right-[278px] hidden transition-all duration-300 ease-out lg:block"
              style={{
                width: '150px',
                top: '200px',
                ...cardAnimations.person1,
              }}
            >
              <Image
                src={person1}
                width={150}
                height={157}
                alt=""
                className="rounded-xl"
              />
            </div>

            <div
              className="absolute left-4 hidden transition-all duration-300 ease-out lg:block"
              style={{
                width: '245px',
                top: '700px',
                ...cardAnimations.person2,
              }}
            >
              <Image
                src={person2}
                width={245}
                height={245}
                alt=""
                className="rounded-xl"
              />
            </div>

            <div
              className="absolute right-4 hidden transition-all duration-300 ease-out lg:block"
              style={{
                width: '267px',
                top: '500px',
                ...cardAnimations.person3,
              }}
            >
              <Image
                src={person3}
                width={267}
                height={292}
                alt=""
                className="rounded-xl"
                style={{ transform: 'rotate(25deg)' }}
              />
            </div>

            {/* Floating Card: Walmart Cash Back */}
            <div
              className="absolute left-8 hidden transition-all duration-300 ease-out lg:block"
              style={{
                top: '200px',
                ...cardAnimations.card1,
              }}
            >
              <div className="flex items-center gap-3 rounded-xl bg-white px-3 py-3 shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0053e2] p-4">
                  <Image
                    src={walmartSpark}
                    width={33}
                    height={37}
                    alt="Walmart"
                  />
                </div>
                <p className="font-display pr-4 text-lg text-[#0a2d4a]">
                  Get 1% cash back
                </p>
              </div>
            </div>

            {/* Floating Card: Get the App */}
            <Link
              href="/download"
              className="fixed bottom-8 left-8 hidden transition-all duration-300 ease-out hover:scale-105 lg:block"
              style={{
                transform: 'translateY(0px)', // No tilt
                opacity: cardAnimations.card2.opacity,
                zIndex: 10,
              }}
            >
              <div className="flex items-center gap-4 rounded-xl bg-white px-3 py-3 shadow-lg">
                <div className="bg-dolphin-200 h-20 w-20 rounded-lg p-2">
                  <QRCodeSVG
                    value={BRANCH_DOWNLOAD_LINK.website}
                    size={64}
                    className="h-full w-full"
                  />
                </div>
                <p className="font-display text-lg leading-tight text-[#0a2d4a]">
                  Get the
                  <br />
                  ATM App
                </p>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-500">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 9H15M15 9L9 3M15 9L9 15"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </Link>

            {/* Floating Card: Transaction */}
            <div
              className="absolute right-0 hidden transition-all duration-300 ease-out lg:block"
              style={{
                top: '750px',
                ...cardAnimations.card3,
              }}
            >
              <div className="flex items-center gap-4 rounded-xl bg-white px-3 py-3 shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e1f3e3] p-4">
                  <Image
                    src={lunchIcon}
                    width={28}
                    height={28}
                    alt="Restaurant"
                  />
                </div>
                <div className="flex items-center gap-8">
                  <div>
                    <p className="font-display text-lg text-[#0a3754]">
                      In-n-Out
                    </p>
                    <p className="text-sm text-[#0a3754]/50">Jan 12</p>
                  </div>
                  <p className="font-display text-lg text-[#0a3754]">$36.12</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trusted by Brands Section */}
      <TrustedBy />
      {/* A Brighter Financial Future Section */}
      <BrighterFuture />
      {/* Features Grid Section */}
      <FeaturesGrid />
      {/* Instant Cash Section with Details */}
      <InstantCash />
      {/* Personalized Earning Section */}
      <PersonalizedEarning />
      {/* Bank-Level Security Section */}
      <BankLevelSecurity />
      {/* Testimonials Section */}
      <Testimonials />
      {/* How to Start Section */}
      <HowToStart />
      {/* Ready to Transform CTA Section */}
      <TransformCTA />
      {/* FAQ Section */}
      <FAQLodge />
    </>
  )
}

Index.getLayout = (page: ReactElement) => (
  <MarketingLayout bg="light-blue" mainClassName="overflow-x-hidden">
    {page}
  </MarketingLayout>
)

export default Index
